/**
 * Krok PROP3B — PREDSTAVA MAJITELE JAKO NABIDKOVA CENA JEDNIM KLEPNUTIM.
 *
 * Po kroku PROP3 je cislo ze zalozeni zakazky „predstava majitele"
 * (`sale.owner_price_expectation`) a nabidkovou cenou (`asking_price`) se
 * stane jen na vyslovny pokyn maklere. Kdyz makler nezaskrtl „Toto je
 * nabídková cena", inzerat ani faktura cenu nemely — a NIKDE se nedozvedel
 * proc (zadani PROP3B, R.B. 15. 10.).
 *
 * Tohle je JEDNO MISTO, ktere rozhoduje, jestli se vyzva ukaze a co zapise.
 * Pouzivaji ho dve obrazovky: koncept inzeratu (`listing-draft/DataStep`) a
 * nova faktura za provizi (`invoices`); samotny text je v katalogu
 * `webAdmin.priceExpectation.*`.
 *
 * Vychozi stav zaskrtavatka v pruvodci zustava VYPNUTY — predstava majitele
 * se nesmi sama vydavat za nabidkovou cenu (zadani „Proč").
 */
import { apiClient } from './api.js';
import { unwrap } from './api-error.js';
import { factMoney, type Money, type PropertyFact } from './properties.js';

/** Klic predstavy majitele v registru `FACT_KEYS` (krok PROP3 bod 1). */
export const OWNER_PRICE_EXPECTATION_FACT_KEY = 'sale.owner_price_expectation';

/** Klic NABIDKOVE ceny — jediny, ze ktereho cte inzerat a zaklad provize. */
export const ASKING_PRICE_FACT_KEY = 'asking_price';

/**
 * Co ukazat u pole „Cena":
 *  - `offer` = nabidkova cena chybi, ale predstava majitele je — nabidni ji;
 *  - `missing` = neni ani predstava, takze jen rekni, ze cena chybi;
 *  - `null` = cena je, neni co resit.
 */
export type PriceExpectationPrompt =
  | { readonly kind: 'offer'; readonly expectation: Money }
  | { readonly kind: 'missing' };

export function priceExpectationPrompt(input: {
  /** Ma uz obrazovka nabidkovou cenu (vyplnene pole / predvyplneny zaklad)? */
  readonly hasAskingPrice: boolean;
  /** `undefined` = starsi odpoved serveru, ktera predstavu majitele neposila. */
  readonly ownerExpectation: Money | null | undefined;
}): PriceExpectationPrompt | null {
  if (input.hasAskingPrice) return null;
  const expectation = input.ownerExpectation;
  return expectation == null ? { kind: 'missing' } : { kind: 'offer', expectation };
}

/** Predstava majitele z faktu nemovitosti (`GET /v1/properties/:id/facts`). */
export function ownerPriceExpectationOf(facts: readonly PropertyFact[] | undefined): Money | null {
  return factMoney(facts, OWNER_PRICE_EXPECTATION_FACT_KEY);
}

/**
 * Zapise predstavu majitele jako NABIDKOVOU cenu — az na klepnuti maklere.
 *
 * Puvod je „makléř" (`sourceType: 'user'`, status jiny nez `VERIFIED` →
 * `originOfProvenance` vraci `broker`) a cas zapisu je cas klepnuti
 * (`property_facts.valid_from` dava server). Tataz cesta, jakou pise ulozene
 * pole v konceptu inzeratu (`listing-draft-ports.ts` `writePropertyFacts`).
 *
 * Koncept inzeratu tuhle funkci NEVOLA: tam jde ulozeni pres
 * `saveListingDraft` (PATCH konceptu), ktery fakt zapise na serveru v teze
 * transakci jako audit a zaroven vrati prepocitany pohled — obejit ho by
 * znamenalo ukazovat stary nazev a starou kontrolu. Rozhodnuti „kdy a co"
 * je presto JEDNO (`priceExpectationPrompt` vyse).
 */
export async function adoptOwnerPriceExpectationAsAsking(propertyId: string, expectation: Money): Promise<void> {
  await unwrap(
    apiClient.PUT('/v1/properties/{id}/facts', {
      params: { path: { id: propertyId } },
      body: {
        factKey: ASKING_PRICE_FACT_KEY,
        value: { amount: expectation.amount, currency: expectation.currency },
        provenance: { status: 'UNKNOWN', sourceType: 'user', sourceRef: 'owner-price-expectation' },
      },
    }),
  );
}
